import { getAuth } from "firebase/auth";
import AuthManager from "../managers/AuthManager";

const getBaseUrl = () => {
  if (process.env.NODE_ENV === "development") return "http://localhost:8000";
  return "https://www.d3-dashboard.com";
};

export const API_BASE_URL = getBaseUrl();

const normalizeEndpoint = (endpoint) => {
  if (!endpoint) throw new Error("fetchAPI: endpoint is required");
  return endpoint.startsWith("/api/")
    ? endpoint
    : `/api${endpoint.startsWith("/") ? "" : "/"}${endpoint}`;
};

const isAbortError = (err) =>
  err?.name === "AbortError" ||
  err?.code === 20 ||
  String(err?.message || "").toLowerCase().includes("aborted");

const throwIfAborted = (signal) => {
  if (signal?.aborted) {
    const e = new Error("The operation was aborted.");
    e.name = "AbortError";
    throw e;
  }
};

const getAuthToken = async (signal) => {
  throwIfAborted(signal);

  await AuthManager.waitForAuth();
  throwIfAborted(signal);

  const auth = getAuth();

  if (!auth.currentUser) {
    await AuthManager.ensureUser("api");
    throwIfAborted(signal);
  }

  if (!auth.currentUser) return "";

  try {
    return await auth.currentUser.getIdToken();
  } catch (error) {
    console.warn("Failed to get auth token:", error);
    return "";
  }
};

const safeReadJson = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const buildHeaders = (authToken, options) => {
  const headers = {
    ...(options?.headers || {}),
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  };

  const isForm = options?.body instanceof FormData;
  if (!isForm && !("Content-Type" in headers)) {
    headers["Content-Type"] = "application/json";
  }

  if (headers["Content-Type"] == null) delete headers["Content-Type"];

  return headers;
};

export const fetchAPI = async (endpoint, options = {}) => {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const url = `${API_BASE_URL}${normalizedEndpoint}`;

  const signal = options.signal;
  throwIfAborted(signal);

  try {
    const authToken = await getAuthToken(signal);

    const fetchOptions = {
      mode: "cors",
      ...options,
      headers: buildHeaders(authToken, options),
      signal,
    };

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      const errorData = await safeReadJson(response);
      const message =
        errorData?.error ||
        errorData?.message ||
        `API call failed: ${response.status} ${response.statusText}`;

      const err = new Error(message);
      err.status = response.status;
      err.statusText = response.statusText;
      err.data = errorData;
      throw err;
    }

    if (response.status === 204) return null;

    const ct = response.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const maybeJson = await safeReadJson(response);
      if (maybeJson !== null) return maybeJson;
      return null;
    }

    return response.json();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    if (error?.status) throw error;
    
    const msg = String(error?.message || "");
    if (error?.name === "TypeError" && msg.toLowerCase().includes("cors")) {
      const corsError = new Error(
        "CORS error: Server is not allowing requests from this origin."
      );
      corsError.status = 0;
      corsError.originalError = error;
      throw corsError;
    }

    const networkError = new Error(msg || "Network error. Please check your connection.");
    networkError.status = 0;
    networkError.originalError = error;
    throw networkError;
  }
};

export const fetchAPIBlob = async (endpoint, options = {}) => {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const url = `${API_BASE_URL}${normalizedEndpoint}`;

  const signal = options.signal;
  throwIfAborted(signal);

  try {
    const authToken = await getAuthToken(signal);

    const fetchOptions = {
      mode: "cors",
      ...options,
      headers: buildHeaders(authToken, options),
      signal,
    };

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      const errorData = await safeReadJson(response);
      const message =
        errorData?.error ||
        errorData?.message ||
        `API call failed: ${response.status} ${response.statusText}`;

      const err = new Error(message);
      err.status = response.status;
      err.statusText = response.statusText;
      err.data = errorData;
      throw err;
    }

    return response.blob();
  } catch (error) {
    if (isAbortError(error)) throw error;
    if (error?.status) throw error;

    const networkError = new Error(
      String(error?.message || "Network error. Please check your connection.")
    );
    networkError.status = 0;
    networkError.originalError = error;
    throw networkError;
  }
};
